import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Autocomplete, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton, InputAdornment, Paper, Stack, Tab, Tabs, TextField, Typography } from "@mui/material";
import { useParams } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import {
  assignCoreNetworkBadge,
  createCoreNetworkCapability,
  createCoreNetworkSubscription,
  createCoreNetworkMembership,
  getCoreNetworkMemberRatingSummary,
  getCoreNetworkMember,
  listCoreNetworkBadges,
  listCoreNetworkPayouts,
  listCoreNetworkMemberRatings,
  listCoreNetworkWorkRequests,
  revokeCoreNetworkBadge,
  sendCoreNetworkPortalInvite,
  sendTalentPasswordReset,
  generateTalentTemporaryPassword,
  setTalentTemporaryPassword,
  updateCoreNetworkPortalAccess,
  updateCoreNetworkMembership,
  updateCoreNetworkSubscription,
  upsertCoreNetworkCapacity,
} from "../../../services/coreNetwork";
import { getDecoratorsDetails, updateDecoratorDetails } from "../../../services/decorator";
import { AuthContext } from "../../../helpers/AuthContext";
import { CORE_NETWORK_PERMISSIONS, hasAnyCoreNetworkPermission, hasCoreNetworkPermission } from "../../../helpers/coreNetworkPermissions";
import {
  CoreNetworkMemberAvatar,
  CoreNetworkShell,
  CORE_NETWORK_LABEL,
  ErrorState,
  JsonListChips,
  KeyValueGrid,
  LoadingState,
  SectionCard,
  StatusChip,
  PagedTable,
  formatCurrency,
  formatDateTime,
  normalizeJsonObject,
  safeArray,
  summarizeAssignmentsForMember,
  toTitleCase,
} from "./coreNetworkUiShared";
import CoreNetworkCapabilityPanel from "./components/CoreNetworkCapabilityPanel";
import CoreNetworkCapacityPanel from "./components/CoreNetworkCapacityPanel";
import CoreNetworkMembershipPanel from "./components/CoreNetworkMembershipPanel";
import CoreNetworkAssignmentPanel from "./components/CoreNetworkAssignmentPanel";
import CoreNetworkRatingPanel from "./components/CoreNetworkRatingPanel";
import CoreNetworkBadgePanel from "./components/CoreNetworkBadgePanel";
import CoreNetworkSubscriptionPanel from "./components/CoreNetworkSubscriptionPanel";
import "./CoreNetworkTalentDetail.css";

export default function CoreNetworkTalentDetail() {
  const { instanceUniqueId, id } = useParams();
  const { authState } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [error, setError] = useState("");
  const [member, setMember] = useState(null);
  const [workRequests, setWorkRequests] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [badges, setBadges] = useState([]);
  const [decorators, setDecorators] = useState([]);
  const [approvalDecoratorId, setApprovalDecoratorId] = useState(null);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [passwordReason, setPasswordReason] = useState("");
  const [passwordOptions, setPasswordOptions] = useState({ sendEmail: true, bccAdministrator: false, requirePasswordChange: true });
  const [manualPassword, setManualPassword] = useState({ password: "", confirmPassword: "", show: false });
  const [oneTimePassword, setOneTimePassword] = useState(null);
  const [passwordMessage, setPasswordMessage] = useState("");
  const canManageMemberships = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_MEMBERSHIPS, instanceUniqueId);
  const canManageCapabilities = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_CAPABILITIES, instanceUniqueId);
  const canManagePayment = hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_PAYMENT, instanceUniqueId);
  const canManageCredentials = canManageMemberships;
  const canViewPlacement = hasAnyCoreNetworkPermission(
    authState,
    [CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS],
    instanceUniqueId
  );

  const loadDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [memberResponse, workRequestResponse, ratingsResponse, summaryResponse, payoutsResponse, badgesResponse, decoratorsResponse] = await Promise.all([
        getCoreNetworkMember(id, instanceUniqueId),
        listCoreNetworkWorkRequests({ instanceUniqueId }),
        listCoreNetworkMemberRatings(id, { instanceUniqueId }),
        getCoreNetworkMemberRatingSummary(id, instanceUniqueId),
        listCoreNetworkPayouts({ instanceUniqueId, memberId: id }),
        listCoreNetworkBadges({ isActive: true }),
        getDecoratorsDetails(instanceUniqueId),
      ]);
      setMember(memberResponse.member || null);
      setWorkRequests(safeArray(workRequestResponse.workRequests));
      setRatings(safeArray(ratingsResponse.ratings));
      setRatingSummary(summaryResponse.summary || null);
      setPayouts(safeArray(payoutsResponse.payouts));
      setBadges(safeArray(badgesResponse.badges));
      setDecorators(safeArray(decoratorsResponse?.result));
    } catch (loadError) {
      setError(loadError?.response?.data?.message || loadError.message || "Unable to load member detail.");
    } finally {
      setLoading(false);
    }
  }, [id, instanceUniqueId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const assignmentSummary = useMemo(
    () => summarizeAssignmentsForMember(workRequests, id),
    [id, workRequests]
  );
  const profileJson = useMemo(() => normalizeJsonObject(member?.profileJson), [member]);
  const metadataJson = useMemo(() => normalizeJsonObject(member?.metadataJson), [member]);
  const routingConfigJson = useMemo(() => normalizeJsonObject(member?.routingConfigJson), [member]);
  const portalAccess = useMemo(() => normalizeJsonObject(member?.portalAccess), [member]);
  const instanceMemberships = useMemo(
    () => safeArray(member?.memberships).filter((membership) => Number(membership.instanceId) > 0 && membership.instance?.instanceUniqueId === instanceUniqueId),
    [instanceUniqueId, member]
  );
  const currentMembership = instanceMemberships[0] || null;
  const selectedApprovalDecorator =
    decorators.find((option) => Number(option.id) === Number(approvalDecoratorId)) || null;
  const linkedDecoratorIsLocalOnly =
    member?.linkedDecorator && member.linkedDecorator.isEliteDecorator !== true;
  const selectedApprovalDecoratorIsLocalOnly =
    selectedApprovalDecorator && selectedApprovalDecorator.isEliteDecorator !== true;
  const scopedMemberBadges = useMemo(
    () => safeArray(member?.badges).filter((badge) => !badge.instance?.instanceUniqueId || badge.instance?.instanceUniqueId === instanceUniqueId),
    [instanceUniqueId, member]
  );
  const scopedSubscriptions = useMemo(
    () => safeArray(member?.subscriptions).filter((subscription) => !subscription.instance?.instanceUniqueId || subscription.instance?.instanceUniqueId === instanceUniqueId),
    [instanceUniqueId, member]
  );
  const capabilityTags = useMemo(
    () => safeArray(safeArray(member?.capabilitiesJson).length ? member?.capabilitiesJson : member?.skillsJson),
    [member]
  );
  const capabilityRecords = useMemo(
    () => safeArray(member?.capabilities).filter((capability) => !capability.instanceId || capability.instanceId === currentMembership?.instanceId),
    [currentMembership?.instanceId, member]
  );
  const capacitySlots = useMemo(
    () => safeArray(member?.capacitySlots).filter((slot) => !slot.instanceId || slot.instanceId === currentMembership?.instanceId),
    [currentMembership?.instanceId, member]
  );
  const completedAssignments = assignmentSummary.completedAssignments;
  const activeAssignments = assignmentSummary.activeAssignments;
  const completedJobCount = Number(ratingSummary?.completedJobCount || completedAssignments.length || 0);
  const activeAssignmentCount = activeAssignments.length;
  const averageOnTimeScore = Number(ratingSummary?.averageOnTimeScore || 0);
  const responseRateValue = averageOnTimeScore > 0 ? `${Math.round((averageOnTimeScore / 5) * 100)}%` : "No data";
  const responseRateHelper = averageOnTimeScore >= 4.5 ? "Excellent" : averageOnTimeScore >= 3.5 ? "Strong" : averageOnTimeScore > 0 ? "Needs review" : "No ratings yet";
  const joinedAt = member?.createdAt || currentMembership?.createdAt || member?.updatedAt;
  const headlineMembership = currentMembership?.membershipType || currentMembership?.role || member?.profileType || member?.memberType || "Provider";
  const talentName = member?.displayName || member?.companyName || `Talent #${member?.id || id}`;
  const heroSubtitle = [
    member?.companyName && member?.companyName !== member?.displayName ? member.companyName : null,
    toTitleCase(headlineMembership),
  ].filter(Boolean).join(" | ");

  const withSaving = async (key, callback) => {
    try {
      setSaving(key);
      await callback();
      await loadDetail();
    } catch (actionError) {
      setError(actionError?.response?.status === 403
        ? "You do not have permission to manage Talent Portal passwords."
        : actionError?.response?.data?.message || actionError.message || "Save failed.");
    } finally {
      setSaving("");
    }
  };

  const closePasswordDialog = () => {
    setPasswordDialog(null);
    setPasswordReason("");
    setManualPassword({ password: "", confirmPassword: "", show: false });
    setPasswordOptions({ sendEmail: true, bccAdministrator: false, requirePasswordChange: true });
  };

  const handlePasswordAction = async () => {
    if (!passwordReason.trim()) {
      setError("A reason is required for Talent Portal password management.");
      return;
    }
    if (passwordDialog === "manual" && manualPassword.password !== manualPassword.confirmPassword) {
      setError("Temporary password confirmation does not match.");
      setManualPassword((prev) => ({ ...prev, password: "", confirmPassword: "" }));
      return;
    }
    await withSaving(`password-${passwordDialog}`, async () => {
      let response;
      if (passwordDialog === "reset") {
        response = await sendTalentPasswordReset(member.id, { reason: passwordReason, sendEmail: true, bccAdministrator: passwordOptions.bccAdministrator });
        setPasswordMessage(response.passwordResetStatus === "sent" ? "Secure password reset sent." : "Secure password reset created.");
      } else if (passwordDialog === "generate") {
        response = await generateTalentTemporaryPassword(member.id, { reason: passwordReason, sendEmail: passwordOptions.sendEmail, bccAdministrator: passwordOptions.bccAdministrator });
        setOneTimePassword({ value: response.temporaryPassword, expiresAt: response.expiresAt, sessionsRevoked: response.sessionsRevoked });
        setPasswordMessage("Temporary password generated. Copy it before closing the dialog.");
      } else if (passwordDialog === "manual") {
        response = await setTalentTemporaryPassword(member.id, {
          password: manualPassword.password,
          confirmPassword: manualPassword.confirmPassword,
          reason: passwordReason,
          sendEmail: passwordOptions.sendEmail,
          bccAdministrator: passwordOptions.bccAdministrator,
          requirePasswordChange: passwordOptions.requirePasswordChange,
        });
        setPasswordMessage(`Temporary password set. ${response.sessionsRevoked || 0} session(s) revoked.`);
      }
      if (passwordDialog !== "generate") closePasswordDialog();
      if (passwordDialog === "manual") setManualPassword({ password: "", confirmPassword: "", show: false });
    });
  };

  const closeOneTimePassword = () => {
    setOneTimePassword(null);
    closePasswordDialog();
  };

  return (
    <CoreNetworkShell
      title="Talent Detail"
      breadcrumb={`${CORE_NETWORK_LABEL} / Talent / Detail`}
    >
      {loading ? <LoadingState label="Loading talent detail..." /> : null}
      {!loading && error ? <ErrorState message={error} onAction={loadDetail} /> : null}
      {!loading && !error && member ? (
        <Stack spacing={3} className="core-network-talent-detail">
          <Paper elevation={0} className="core-network-talent-detail__hero">
            <div className="core-network-talent-detail__hero-main">
              <div className="core-network-talent-detail__hero-identity">
                <CoreNetworkMemberAvatar
                  member={member}
                  size={104}
                  sx={{ borderRadius: 6, border: "1px solid #d6dce9", boxShadow: "0 10px 24px rgba(15, 23, 42, 0.08)" }}
                />
                <div className="core-network-talent-detail__hero-copy">
                  <Typography className="core-network-talent-detail__eyebrow">Core Network Talent Profile</Typography>
                  <Typography className="core-network-talent-detail__hero-title">{talentName}</Typography>
                  <Typography className="core-network-talent-detail__hero-subtitle">
                    {heroSubtitle || "Independent provider profile"}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <StatusChip value={member.globalStatus} />
                    {member.profileType ? <StatusChip value={member.profileType} /> : null}
                    {member.providerKey ? <StatusChip value={member.providerKey} /> : null}
                    {currentMembership?.status ? <StatusChip value={currentMembership.status} /> : null}
                    {metadataJson.defaultProofProvider ? <StatusChip value="default proofing provider" /> : null}
                  </Stack>
                  <JsonListChips values={member.networkTypesJson} emptyLabel="No network tags" />
                  <div className="core-network-talent-detail__hero-facts">
                    <Typography className="core-network-talent-detail__fact">{member.email || "No email on file"}</Typography>
                    <Typography className="core-network-talent-detail__fact">{member.phone || "No phone on file"}</Typography>
                    <Typography className="core-network-talent-detail__fact">{profileJson.city || member.country || "Location unavailable"}</Typography>
                    <Typography className="core-network-talent-detail__fact">Joined {formatDateTime(joinedAt)}</Typography>
                  </div>
                </div>
              </div>

              <div className="core-network-talent-detail__hero-actions">
                {canManageMemberships ? (
                  <Button
                    variant="outlined"
                    disabled={saving === "portal-invite" || !member.email}
                    onClick={() =>
                      withSaving("portal-invite", () =>
                        sendCoreNetworkPortalInvite(member.id, {
                          instanceUniqueId,
                          sendEmail: true,
                        })
                      )
                    }
                  >
                    {portalAccess.status === "active" ? "Send Login Link" : portalAccess.status === "invitation_sent" ? "Resend Invitation" : "Send Invitation"}
                  </Button>
                ) : null}
                {canManageCredentials ? (
                  <Button variant="contained" disabled={saving.startsWith("password-") || !member.email} onClick={() => setPasswordDialog("reset")}>
                    Reset Password
                  </Button>
                ) : null}
                {canManageMemberships ? (
                  portalAccess.status === "disabled" ? (
                    <Button
                      variant="contained"
                      color="success"
                      disabled={saving === "portal-access"}
                      onClick={() =>
                        withSaving("portal-access", () =>
                          updateCoreNetworkPortalAccess(member.id, {
                            enabled: true,
                          })
                        )
                      }
                    >
                      Reactivate Access
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="warning"
                      disabled={saving === "portal-access"}
                      onClick={() =>
                        withSaving("portal-access", () =>
                          updateCoreNetworkPortalAccess(member.id, {
                            enabled: false,
                          })
                        )
                      }
                    >
                      Disable Access
                    </Button>
                  )
                ) : null}
              </div>
            </div>

            <div className="core-network-talent-detail__hero-summary">
              <div className="core-network-talent-detail__summary-card">
                <Typography className="core-network-talent-detail__summary-label">Overall Rating</Typography>
                <Typography className="core-network-talent-detail__summary-value">
                  {ratingSummary?.ratingCount ? (ratingSummary.averageOverallScore ?? "-") : "No data"}
                </Typography>
              </div>
              <div className="core-network-talent-detail__summary-card">
                <Typography className="core-network-talent-detail__summary-label">Completed Jobs</Typography>
                <Typography className="core-network-talent-detail__summary-value">{completedJobCount}</Typography>
              </div>
              <div className="core-network-talent-detail__summary-card">
                <Typography className="core-network-talent-detail__summary-label">Active Assignments</Typography>
                <Typography className="core-network-talent-detail__summary-value">{activeAssignmentCount}</Typography>
              </div>
              <div className="core-network-talent-detail__summary-card">
                <Typography className="core-network-talent-detail__summary-label">Capabilities</Typography>
                <Typography className="core-network-talent-detail__summary-value">{Math.max(capabilityTags.length, capabilityRecords.length)}</Typography>
              </div>
            </div>
          </Paper>

          <div className="core-network-talent-detail__metric-row">
            <Paper elevation={0} className="core-network-talent-detail__metric-card">
              <Typography className="core-network-talent-detail__metric-label">Overall Rating</Typography>
              <Typography className="core-network-talent-detail__metric-value" variant="h3">
                {ratingSummary?.ratingCount ? Number(ratingSummary.averageOverallScore || 0).toFixed(2) : "0.00"}
              </Typography>
              <Typography className="core-network-talent-detail__metric-helper">
                {ratingSummary?.ratingCount ? `Out of ${ratingSummary.ratingCount} ratings` : "No ratings submitted yet"}
              </Typography>
            </Paper>
            <Paper elevation={0} className="core-network-talent-detail__metric-card">
              <Typography className="core-network-talent-detail__metric-label">Completed Jobs</Typography>
              <Typography className="core-network-talent-detail__metric-value" variant="h3">{completedJobCount}</Typography>
              <Typography className="core-network-talent-detail__metric-helper">Finished assignments in this instance</Typography>
            </Paper>
            <Paper elevation={0} className="core-network-talent-detail__metric-card">
              <Typography className="core-network-talent-detail__metric-label">Active Assignments</Typography>
              <Typography className="core-network-talent-detail__metric-value" variant="h3">{activeAssignmentCount}</Typography>
              <Typography className="core-network-talent-detail__metric-helper">Current in-flight work</Typography>
            </Paper>
            <Paper elevation={0} className="core-network-talent-detail__metric-card">
              <Typography className="core-network-talent-detail__metric-label">Response / On-time</Typography>
              <Typography className="core-network-talent-detail__metric-value" variant="h3">{responseRateValue}</Typography>
              <Typography className="core-network-talent-detail__metric-helper">{responseRateHelper}</Typography>
            </Paper>
          </div>

          <Paper elevation={0} className="core-network-talent-detail__tab-shell">
            <Box className="core-network-talent-detail__tabs">
              <Tabs value={activeTab} onChange={(_event, nextValue) => setActiveTab(nextValue)} variant="scrollable" scrollButtons="auto">
                <Tab value="overview" label="Overview" />
                <Tab value="work" label="Work & Capabilities" />
                <Tab value="access" label="Access & Memberships" />
                <Tab value="performance" label="Performance" />
              </Tabs>
            </Box>

            <Box className="core-network-talent-detail__tab-panel">
              {activeTab === "overview" ? (
                <div className="core-network-talent-detail__card-grid">
                  <div className="core-network-talent-detail__span-4">
                    <SectionCard title="About / Contact" subtitle="Profile, routing, and location details.">
                      <KeyValueGrid
                        items={[
                          { label: "Profile type", value: member.profileType ? toTitleCase(member.profileType) : "-" },
                          { label: "Provider key", value: member.providerKey ? toTitleCase(member.providerKey) : "-" },
                          { label: "Email", value: member.email || "-" },
                          { label: "Phone", value: member.phone || "-" },
                          { label: "Timezone", value: member.timezone || "-" },
                          { label: "Country", value: member.country || "-" },
                          { label: "Profile city", value: profileJson.city || "-" },
                          { label: "Profile summary", value: profileJson.summary || profileJson.description || "-" },
                        ]}
                      />
                    </SectionCard>
                  </div>
                  <div className="core-network-talent-detail__span-4">
                    <SectionCard title="Talent Portal Access" subtitle="Current Talent Portal access state and verification history.">
                      <Stack spacing={1.5}>
                        {!member.email ? <Alert severity="warning">A normalized email address is required before a portal invitation can be sent.</Alert> : null}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <StatusChip value={portalAccess.status || "not_invited"} />
                          {portalAccess.credentialStatus ? <StatusChip value={portalAccess.credentialStatus} /> : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {portalAccess.status === "not_invited"
                            ? "Talent portal access has not been invited yet."
                            : portalAccess.status === "invitation_sent"
                              ? "A verification link has been sent and is awaiting password setup."
                              : portalAccess.status === "invitation_expired"
                                ? "The previous verification link expired. Resend the invitation to issue a fresh one."
                                : portalAccess.status === "disabled"
                                  ? "Portal login is disabled for this talent account."
                                  : "Talent portal access is active."}
                        </Typography>
                        <KeyValueGrid
                          items={[
                            { label: "Routing mode", value: routingConfigJson.mode ? toTitleCase(routingConfigJson.mode) : "-" },
                            { label: "Invitation sent", value: formatDateTime(portalAccess.invitedAt) },
                            { label: "Invitation expires", value: formatDateTime(portalAccess.invitationExpiresAt) },
                            { label: "Verified at", value: formatDateTime(portalAccess.verifiedAt) },
                            { label: "Last login", value: formatDateTime(portalAccess.lastLoginAt) },
                          ]}
                        />
                      </Stack>
                    </SectionCard>
                  </div>
                  <div className="core-network-talent-detail__span-4">
                    <CoreNetworkMembershipPanel
                      memberships={instanceMemberships}
                      member={member}
                      decoratorOptions={decorators}
                      saving={saving === "membership"}
                      onCreateMembership={
                        !currentMembership && canManageMemberships
                          ? ((payload) =>
                              withSaving("membership", () =>
                                createCoreNetworkMembership(member.id, { ...payload, instanceUniqueId })
                              ))
                          : null
                      }
                      canManagePayment={canManagePayment}
                      canManagePlacement={canViewPlacement}
                    />
                  </div>
                  {canManageMemberships && currentMembership ? (
                    <div className="core-network-talent-detail__span-4">
                      <SectionCard
                        title="Decorator Approval"
                        subtitle="Approve or link this provider for decoration-production work."
                      >
                        <Stack spacing={2}>
                          <Typography variant="body2" color="text.secondary">
                            Current status: {currentMembership.isEliteCoreNetworkDecorator ? "Approved Elite Core decorator" : "Not approved for decoration-production work"}
                          </Typography>
                          {member.linkedDecorator ? (
                            <Alert severity={linkedDecoratorIsLocalOnly ? "warning" : "success"}>
                              Linked Decorator: {member.linkedDecorator.title}
                              {linkedDecoratorIsLocalOnly ? " This decorator is currently local/private. Promote to Elite Decorator before using in Core Network." : ""}
                            </Alert>
                          ) : (
                            <Alert severity="warning">
                              This member is not yet linked to an Elite Core Decorator record. Link an existing Decorator or create a new linked Decorator before approving decoration-production work.
                            </Alert>
                          )}

                          {!member.linkedDecorator ? (
                            <Stack spacing={1.5}>
                              <Autocomplete
                                options={decorators}
                                value={selectedApprovalDecorator}
                                onChange={(_event, value) => setApprovalDecoratorId(value?.id || null)}
                                getOptionLabel={(option) => option?.title || `Decorator ${option?.id || ""}`}
                                isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    label="Link existing Elite Core Decorator"
                                    helperText="Choose the existing Decorator record to link and approve."
                                  />
                                )}
                              />
                              {selectedApprovalDecoratorIsLocalOnly ? <Alert severity="warning">This decorator is currently local/private. Promote to Elite Decorator before using in Core Network.</Alert> : null}
                            </Stack>
                          ) : null}

                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {currentMembership.isEliteCoreNetworkDecorator ? (
                              <Button
                                variant="outlined"
                                color="warning"
                                onClick={() =>
                                  withSaving("membership-approval", () =>
                                    updateCoreNetworkMembership(currentMembership.id, {
                                      instanceUniqueId,
                                      isEliteCoreNetworkDecorator: false,
                                    })
                                  )
                                }
                              >
                                Remove Elite Decorator Approval
                              </Button>
                            ) : null}

                            {!currentMembership.isEliteCoreNetworkDecorator && member.linkedDecorator ? (
                              <>
                                <Button
                                  variant="contained"
                                  disabled={linkedDecoratorIsLocalOnly}
                                  onClick={() =>
                                    withSaving("membership-approval", () =>
                                      updateCoreNetworkMembership(currentMembership.id, {
                                        instanceUniqueId,
                                        isEliteCoreNetworkDecorator: true,
                                      })
                                    )
                                  }
                                >
                                  Approve For Decoration Production
                                </Button>
                                {linkedDecoratorIsLocalOnly ? (
                                  <Button
                                    variant="outlined"
                                    onClick={() =>
                                      withSaving("decorator-promotion", () =>
                                        updateDecoratorDetails(
                                          member.linkedDecorator.id,
                                          { isEliteDecorator: true },
                                          instanceUniqueId
                                        )
                                      )
                                    }
                                  >
                                    Promote To Elite Decorator
                                  </Button>
                                ) : null}
                              </>
                            ) : null}

                            {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator ? (
                              <Button
                                variant="contained"
                                disabled={!approvalDecoratorId || selectedApprovalDecoratorIsLocalOnly}
                                onClick={() =>
                                  withSaving("membership-approval", () =>
                                    updateCoreNetworkMembership(currentMembership.id, {
                                      instanceUniqueId,
                                      isEliteCoreNetworkDecorator: true,
                                      sourceType: "decorator",
                                      sourceId: approvalDecoratorId,
                                    })
                                  )
                                }
                              >
                                Link Existing Decorator And Approve
                              </Button>
                            ) : null}

                            {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator && selectedApprovalDecoratorIsLocalOnly ? (
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  withSaving("decorator-promotion", () =>
                                    updateDecoratorDetails(
                                      approvalDecoratorId,
                                      { isEliteDecorator: true },
                                      instanceUniqueId
                                    )
                                  )
                                }
                              >
                                Promote Selected Decorator
                              </Button>
                            ) : null}

                            {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator ? (
                              <Button
                                variant="outlined"
                                onClick={() =>
                                  withSaving("membership-approval", () =>
                                    updateCoreNetworkMembership(currentMembership.id, {
                                      instanceUniqueId,
                                      isEliteCoreNetworkDecorator: true,
                                      createLinkedDecorator: true,
                                    })
                                  )
                                }
                              >
                                Create New Linked Decorator And Approve
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </SectionCard>
                    </div>
                  ) : null}
                  <div className="core-network-talent-detail__span-4">
                    <SectionCard title="Current Assignments" subtitle="Open execution work for this provider in the instance.">
                      {activeAssignments.length ? (
                        <Stack spacing={1.5}>
                          {activeAssignments.slice(0, 3).map((assignment) => (
                            <Stack key={assignment.id} spacing={0.75} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf1f7" }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {assignment.workRequest?.title || `Work Request #${assignment.workRequestId}`}
                              </Typography>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <StatusChip value={assignment.status} />
                                {assignment.providerStatus ? <StatusChip value={assignment.providerStatus} /> : null}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                Accepted {formatDateTime(assignment.acceptedAt)} | Started {formatDateTime(assignment.startedAt)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No active assignments for this instance.</Typography>
                      )}
                    </SectionCard>
                  </div>
                  {canViewPlacement ? (
                    <div className="core-network-talent-detail__span-4">
                      <SectionCard title="Badges & Placement" subtitle="Placement visibility and subscription coverage.">
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle2">Badges</Typography>
                          <JsonListChips values={scopedMemberBadges.map((entry) => entry.badge?.label || entry.badge?.code).filter(Boolean)} emptyLabel="No badges assigned" />
                          <Typography variant="subtitle2">Subscriptions</Typography>
                          <JsonListChips values={scopedSubscriptions.map((entry) => entry.planCode).filter(Boolean)} emptyLabel="No subscriptions assigned" />
                        </Stack>
                      </SectionCard>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {activeTab === "work" ? (
                <div className="core-network-talent-detail__card-grid">
                  <div className="core-network-talent-detail__span-6">
                    <CoreNetworkCapabilityPanel
                      capabilities={capabilityRecords}
                      saving={saving === "capability"}
                      onCreateCapability={
                        canManageCapabilities
                          ? ((payload) =>
                              withSaving("capability", () =>
                                createCoreNetworkCapability({
                                  ...payload,
                                  memberId: member.id,
                                  membershipId: currentMembership?.id,
                                  instanceUniqueId,
                                })
                              ))
                          : null
                      }
                    />
                  </div>
                  <div className="core-network-talent-detail__span-6">
                    <CoreNetworkCapacityPanel
                      slots={capacitySlots}
                      saving={saving === "capacity"}
                      onSaveSlot={
                        canManageCapabilities
                          ? ((payload) =>
                              withSaving("capacity", () =>
                                upsertCoreNetworkCapacity({
                                  ...payload,
                                  memberId: member.id,
                                  membershipId: currentMembership?.id,
                                  instanceUniqueId,
                                })
                              ))
                          : null
                      }
                    />
                  </div>
                  <div className="core-network-talent-detail__span-12">
                    <CoreNetworkAssignmentPanel assignments={activeAssignments.map((item) => ({
                      ...item,
                      member,
                      membership: currentMembership,
                    }))} canViewPayment={canManagePayment} />
                  </div>
                  <div className="core-network-talent-detail__span-12">
                    <SectionCard title="Completed Assignments" subtitle="Historical completed work for this member in the current instance.">
                      {completedAssignments.length ? (
                        <Stack spacing={1.5}>
                          {completedAssignments.map((assignment) => (
                            <Stack key={assignment.id} spacing={0.5} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf1f7" }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {assignment.workRequest?.title || `Work Request #${assignment.workRequestId}`}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {toTitleCase(assignment.status)} | {formatDateTime(assignment.completedAt)}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">No completed assignments for this instance yet.</Typography>
                      )}
                    </SectionCard>
                  </div>
                </div>
              ) : null}

              {activeTab === "access" ? (
                <div className="core-network-talent-detail__card-grid">
                  {canManageMemberships ? (
                    <div className="core-network-talent-detail__span-6">
                      <SectionCard title="Portal Access Actions" subtitle="Invite, disable, and reactivate Talent Portal access.">
                        <Stack spacing={2}>
                          {!member.email ? <Alert severity="warning">A normalized email address is required before a portal invitation can be sent.</Alert> : null}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button
                              variant="outlined"
                              disabled={saving === "portal-invite" || !member.email}
                              onClick={() =>
                                withSaving("portal-invite", () =>
                                  sendCoreNetworkPortalInvite(member.id, {
                                    instanceUniqueId,
                                    sendEmail: true,
                                  })
                                )
                              }
                            >
                              {portalAccess.status === "active" ? "Send Login Link" : portalAccess.status === "invitation_sent" ? "Resend Invitation" : "Send Invitation"}
                            </Button>
                            {portalAccess.status === "disabled" ? (
                              <Button
                                variant="contained"
                                color="success"
                                disabled={saving === "portal-access"}
                                onClick={() =>
                                  withSaving("portal-access", () =>
                                    updateCoreNetworkPortalAccess(member.id, {
                                      enabled: true,
                                    })
                                  )
                                }
                              >
                                Reactivate Portal Access
                              </Button>
                            ) : (
                              <Button
                                variant="outlined"
                                color="warning"
                                disabled={saving === "portal-access"}
                                onClick={() =>
                                  withSaving("portal-access", () =>
                                    updateCoreNetworkPortalAccess(member.id, {
                                      enabled: false,
                                    })
                                  )
                                }
                              >
                                Disable Portal Access
                              </Button>
                            )}
                          </Stack>
                        </Stack>
                      </SectionCard>
                    </div>
                  ) : null}
                  {canManageCredentials ? (
                    <div className="core-network-talent-detail__span-6">
                      <SectionCard title="Password Management" subtitle="Reset or issue temporary Talent Portal passwords.">
                        <Stack spacing={2}>
                          {passwordMessage ? <Alert severity="success">{passwordMessage}</Alert> : null}
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="contained" disabled={saving.startsWith("password-") || !member.email} onClick={() => setPasswordDialog("reset")}>Send secure password reset</Button>
                            <Button variant="outlined" color="warning" disabled={saving.startsWith("password-") || !member.email} onClick={() => setPasswordDialog("generate")}>Generate temporary password</Button>
                            <Button variant="outlined" color="warning" disabled={saving.startsWith("password-") || !member.email} onClick={() => setPasswordDialog("manual")}>Set temporary password manually</Button>
                          </Stack>
                          <Alert severity="warning">
                            Temporary passwords replace the current Talent Portal password, revoke existing sessions, and require a password change at next login.
                          </Alert>
                          <KeyValueGrid
                            items={[
                              { label: "Password change required", value: member.mustChangePassword ? "Yes" : "No" },
                              { label: "Temporary password expires", value: formatDateTime(member.temporaryPasswordExpiresAt) },
                              { label: "Last password reset", value: formatDateTime(member.lastPasswordResetAt) },
                              { label: "Password changed", value: formatDateTime(member.passwordChangedAt) },
                            ]}
                          />
                        </Stack>
                      </SectionCard>
                    </div>
                  ) : null}
                  {canViewPlacement ? (
                    <>
                      <div className="core-network-talent-detail__span-6">
                        <CoreNetworkBadgePanel
                          badges={badges}
                          memberBadges={scopedMemberBadges}
                          membershipId={currentMembership?.id}
                          saving={saving === "badge"}
                          canManage={hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, instanceUniqueId)}
                          onAssignBadge={(payload) =>
                            withSaving("badge", () =>
                              assignCoreNetworkBadge(member.id, {
                                ...payload,
                                instanceUniqueId,
                              })
                            )
                          }
                          onRevokeBadge={(memberBadge) =>
                            withSaving("badge", () =>
                              revokeCoreNetworkBadge(memberBadge.id, {
                                instanceUniqueId,
                                reason: "Revoked from internal talent detail",
                              })
                            )
                          }
                        />
                      </div>
                      <div className="core-network-talent-detail__span-6">
                        <CoreNetworkSubscriptionPanel
                          subscriptions={scopedSubscriptions}
                          membershipId={currentMembership?.id}
                          saving={saving === "subscription"}
                          canManage={hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS, instanceUniqueId)}
                          onCreateSubscription={(payload) =>
                            withSaving("subscription", () =>
                              createCoreNetworkSubscription(member.id, {
                                ...payload,
                                instanceUniqueId,
                              })
                            )
                          }
                          onUpdateSubscription={(subscriptionId, payload) =>
                            withSaving("subscription", () =>
                              updateCoreNetworkSubscription(subscriptionId, {
                                ...payload,
                                instanceUniqueId,
                              })
                            )
                          }
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              ) : null}

              {activeTab === "performance" ? (
                <div className="core-network-talent-detail__card-grid">
                  <div className="core-network-talent-detail__span-7">
                    <CoreNetworkRatingPanel
                      assignments={[]}
                      ratings={ratings}
                      ratingSummary={ratingSummary}
                    />
                  </div>
                  <div className="core-network-talent-detail__span-5">
                    <div className="core-network-talent-detail__plain-section">
                      <Typography className="core-network-talent-detail__section-title">Ratings Summary</Typography>
                      <Typography className="core-network-talent-detail__section-subtitle">A quick read on quality, timing, and communication.</Typography>
                      {ratingSummary?.ratingCount ? (
                        <div className="core-network-talent-detail__rating-list">
                          {[
                            ["Overall Rating", ratingSummary.averageOverallScore ?? "-"],
                            ["Quality", ratingSummary.averageQualityScore ?? "-"],
                            ["On Time", ratingSummary.averageOnTimeScore ?? "-"],
                            ["Communication", ratingSummary.averageCommunicationScore ?? "-"],
                            ["Defect", ratingSummary.averageDefectScore ?? "-"],
                            ["Completed Jobs", ratingSummary.completedJobCount ?? 0],
                          ].map(([label, value]) => (
                            <div className="core-network-talent-detail__rating-row" key={label}>
                              <Typography variant="body2">{label}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>{value}</Typography>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          No ratings yet for this provider in the current instance.
                        </Typography>
                      )}
                    </div>
                  </div>
                  {canManagePayment ? (
                    <div className="core-network-talent-detail__span-12">
                      <SectionCard title="Payout Tracking" subtitle="Instance-scoped payout history for this provider.">
                        <PagedTable
                          rows={payouts}
                          columns={[
                            {
                              key: "workRequestId",
                              label: "Work Request",
                              render: (row) => row.workRequest?.title || `Work Request #${row.workRequestId}`,
                            },
                            { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
                            { key: "amount", label: "Amount", render: (row) => formatCurrency(row.amount, row.currency || "CAD") },
                            { key: "payoutMethod", label: "Method", render: (row) => row.payoutMethod || "-" },
                            { key: "approvedAt", label: "Approved", render: (row) => formatDateTime(row.approvedAt) },
                            { key: "paidAt", label: "Paid", render: (row) => formatDateTime(row.paidAt) },
                          ]}
                          emptyTitle="No payouts tracked yet"
                          emptyDescription="Payouts for this provider will appear here once assignment payout records are created."
                        />
                      </SectionCard>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Box>
          </Paper>

          <Dialog open={Boolean(passwordDialog)} onClose={saving.startsWith("password-") ? undefined : closePasswordDialog} fullWidth maxWidth="sm">
            <DialogTitle>
              {passwordDialog === "reset" ? "Send secure password reset" : passwordDialog === "generate" ? "Generate temporary password" : "Set temporary password manually"}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {passwordDialog === "reset" ? (
                  <Alert severity="info">This sends the talent member a secure, single-use password-reset link. It does not reveal or change their password inside Elite Core.</Alert>
                ) : null}
                {passwordDialog === "generate" ? (
                  <Alert severity="warning">This will replace the talent member’s current password, revoke existing Talent Portal sessions, and require the talent member to change the temporary password at next login.</Alert>
                ) : null}
                {passwordDialog === "manual" ? (
                  <>
                    <TextField
                      label="New temporary password"
                      type={manualPassword.show ? "text" : "password"}
                      value={manualPassword.password}
                      onChange={(event) => setManualPassword((prev) => ({ ...prev, password: event.target.value }))}
                      autoComplete="new-password"
                      required
                      InputProps={{ endAdornment: <InputAdornment position="end"><IconButton aria-label={manualPassword.show ? "Hide password" : "Show password"} onClick={() => setManualPassword((prev) => ({ ...prev, show: !prev.show }))}>{manualPassword.show ? <VisibilityOffIcon /> : <VisibilityIcon />}</IconButton></InputAdornment> }}
                    />
                    <TextField label="Confirm temporary password" type={manualPassword.show ? "text" : "password"} value={manualPassword.confirmPassword} onChange={(event) => setManualPassword((prev) => ({ ...prev, confirmPassword: event.target.value }))} autoComplete="new-password" required />
                    <Alert severity="info">At least 8 characters • At least one uppercase letter • At least one lowercase letter • At least one number • At least one special character</Alert>
                  </>
                ) : null}
                <TextField label="Reason" value={passwordReason} onChange={(event) => setPasswordReason(event.target.value)} required multiline minRows={2} />
                {passwordDialog !== "reset" ? <FormControlLabel control={<Checkbox checked={passwordOptions.sendEmail} onChange={(event) => setPasswordOptions((prev) => ({ ...prev, sendEmail: event.target.checked }))} />} label="Email the talent member that their password was reset" /> : null}
                {passwordDialog === "manual" ? <FormControlLabel control={<Checkbox checked={passwordOptions.requirePasswordChange} onChange={(event) => setPasswordOptions((prev) => ({ ...prev, requirePasswordChange: event.target.checked }))} />} label="Require password change at next login" /> : null}
                <FormControlLabel control={<Checkbox checked={passwordOptions.bccAdministrator} onChange={(event) => setPasswordOptions((prev) => ({ ...prev, bccAdministrator: event.target.checked }))} />} label="Send BCC confirmation to configured administrator address" />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closePasswordDialog} disabled={saving.startsWith("password-")}>Cancel</Button>
              <Button onClick={handlePasswordAction} variant="contained" disabled={saving.startsWith("password-") || !passwordReason.trim()}>
                {saving.startsWith("password-") ? "Submitting..." : passwordDialog === "reset" ? "Send reset email" : passwordDialog === "generate" ? "Generate temporary password" : "Set temporary password"}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(oneTimePassword?.value)} onClose={() => undefined} fullWidth maxWidth="sm">
            <DialogTitle>Temporary password</DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Alert severity="warning">This password cannot be retrieved after this dialog is closed.</Alert>
                <TextField label="Temporary password" value={oneTimePassword?.value || ""} InputProps={{ readOnly: true }} />
                <Button variant="outlined" onClick={() => navigator.clipboard?.writeText(oneTimePassword?.value || "")}>Copy</Button>
                <Typography variant="body2">Expires: {formatDateTime(oneTimePassword?.expiresAt)}</Typography>
                <Typography variant="body2">Sessions revoked: {oneTimePassword?.sessionsRevoked || 0}</Typography>
                <Typography variant="body2">The talent member must change this password after signing in.</Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" onClick={closeOneTimePassword}>I copied this password</Button>
            </DialogActions>
          </Dialog>

          <CoreNetworkMembershipPanel
            memberships={instanceMemberships}
            member={member}
            decoratorOptions={decorators}
            saving={saving === "membership"}
            onCreateMembership={
              !currentMembership && canManageMemberships
                ? ((payload) =>
                    withSaving("membership", () =>
                      createCoreNetworkMembership(member.id, { ...payload, instanceUniqueId })
                    ))
                : null
            }
            canManagePayment={canManagePayment}
            canManagePlacement={canViewPlacement}
          />

          {canManageMemberships && currentMembership ? (
            <SectionCard
              title="Decorator Approval"
              subtitle="Only approved Elite Core decorators are eligible for Core Network decoration-production matching."
            >
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  Current status: {currentMembership.isEliteCoreNetworkDecorator ? "Approved Elite Core decorator" : "Not approved for decoration-production work"}
                </Typography>
                {member.linkedDecorator ? (
                  <Alert severity={linkedDecoratorIsLocalOnly ? "warning" : "success"}>
                    Linked Decorator: {member.linkedDecorator.title}
                    {linkedDecoratorIsLocalOnly
                      ? " This decorator is currently local/private. Promote to Elite Decorator before using in Core Network."
                      : ""}
                  </Alert>
                ) : (
                  <Alert severity="warning">
                    This member is not yet linked to an Elite Core Decorator record. Link an existing Decorator or create a new linked Decorator before approving decoration-production work.
                  </Alert>
                )}

                {!member.linkedDecorator ? (
                  <Stack spacing={1.5}>
                    <Autocomplete
                      options={decorators}
                      value={selectedApprovalDecorator}
                      onChange={(_event, value) => setApprovalDecoratorId(value?.id || null)}
                      getOptionLabel={(option) => option?.title || `Decorator ${option?.id || ""}`}
                      isOptionEqualToValue={(option, value) => Number(option?.id) === Number(value?.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Link existing Elite Core Decorator"
                          helperText="Choose the existing Decorator record to link and approve."
                        />
                      )}
                    />
                    {selectedApprovalDecoratorIsLocalOnly ? (
                      <Alert severity="warning">
                        This decorator is currently local/private. Promote to Elite Decorator before using in Core Network.
                      </Alert>
                    ) : null}
                  </Stack>
                ) : null}

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {currentMembership.isEliteCoreNetworkDecorator ? (
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() =>
                        withSaving("membership-approval", () =>
                          updateCoreNetworkMembership(currentMembership.id, {
                            instanceUniqueId,
                            isEliteCoreNetworkDecorator: false,
                          })
                        )
                      }
                    >
                      Remove Elite Decorator Approval
                    </Button>
                  ) : null}

                  {!currentMembership.isEliteCoreNetworkDecorator && member.linkedDecorator ? (
                    <>
                      <Button
                        variant="contained"
                        disabled={linkedDecoratorIsLocalOnly}
                        onClick={() =>
                          withSaving("membership-approval", () =>
                            updateCoreNetworkMembership(currentMembership.id, {
                              instanceUniqueId,
                              isEliteCoreNetworkDecorator: true,
                            })
                          )
                        }
                      >
                        Approve For Decoration Production
                      </Button>
                      {linkedDecoratorIsLocalOnly ? (
                        <Button
                          variant="outlined"
                          onClick={() =>
                            withSaving("decorator-promotion", () =>
                              updateDecoratorDetails(
                                member.linkedDecorator.id,
                                { isEliteDecorator: true },
                                instanceUniqueId
                              )
                            )
                          }
                        >
                          Promote To Elite Decorator
                        </Button>
                      ) : null}
                    </>
                  ) : null}

                  {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator ? (
                    <Button
                      variant="contained"
                      disabled={!approvalDecoratorId || selectedApprovalDecoratorIsLocalOnly}
                      onClick={() =>
                        withSaving("membership-approval", () =>
                          updateCoreNetworkMembership(currentMembership.id, {
                            instanceUniqueId,
                            isEliteCoreNetworkDecorator: true,
                            sourceType: "decorator",
                            sourceId: approvalDecoratorId,
                          })
                        )
                      }
                    >
                      Link Existing Decorator And Approve
                    </Button>
                  ) : null}

                  {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator && selectedApprovalDecoratorIsLocalOnly ? (
                    <Button
                      variant="outlined"
                      onClick={() =>
                        withSaving("decorator-promotion", () =>
                          updateDecoratorDetails(
                            approvalDecoratorId,
                            { isEliteDecorator: true },
                            instanceUniqueId
                          )
                        )
                      }
                    >
                      Promote Selected Decorator
                    </Button>
                  ) : null}

                  {!currentMembership.isEliteCoreNetworkDecorator && !member.linkedDecorator ? (
                    <Button
                      variant="outlined"
                      onClick={() =>
                        withSaving("membership-approval", () =>
                          updateCoreNetworkMembership(currentMembership.id, {
                            instanceUniqueId,
                            isEliteCoreNetworkDecorator: true,
                            createLinkedDecorator: true,
                          })
                        )
                      }
                    >
                      Create New Linked Decorator And Approve
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </SectionCard>
          ) : null}

          <Grid container spacing={3}>
            {canViewPlacement ? (
              <Grid item xs={12} lg={6}>
                <CoreNetworkBadgePanel
                  badges={badges}
                  memberBadges={scopedMemberBadges}
                  membershipId={currentMembership?.id}
                  saving={saving === "badge"}
                  canManage={hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_BADGES, instanceUniqueId)}
                  onAssignBadge={(payload) =>
                    withSaving("badge", () =>
                      assignCoreNetworkBadge(member.id, {
                        ...payload,
                        instanceUniqueId,
                      })
                    )
                  }
                  onRevokeBadge={(memberBadge) =>
                    withSaving("badge", () =>
                      revokeCoreNetworkBadge(memberBadge.id, {
                        instanceUniqueId,
                        reason: "Revoked from internal talent detail",
                      })
                    )
                  }
                />
              </Grid>
            ) : null}
            {canViewPlacement ? (
              <Grid item xs={12} lg={6}>
                <CoreNetworkSubscriptionPanel
                  subscriptions={scopedSubscriptions}
                  membershipId={currentMembership?.id}
                  saving={saving === "subscription"}
                  canManage={hasCoreNetworkPermission(authState, CORE_NETWORK_PERMISSIONS.MANAGE_SUBSCRIPTIONS, instanceUniqueId)}
                  onCreateSubscription={(payload) =>
                    withSaving("subscription", () =>
                      createCoreNetworkSubscription(member.id, {
                        ...payload,
                        instanceUniqueId,
                      })
                    )
                  }
                  onUpdateSubscription={(subscriptionId, payload) =>
                    withSaving("subscription", () =>
                      updateCoreNetworkSubscription(subscriptionId, {
                        ...payload,
                        instanceUniqueId,
                      })
                    )
                  }
                />
              </Grid>
            ) : null}
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <CoreNetworkCapabilityPanel
                capabilities={safeArray(member.capabilities).filter((capability) => !capability.instanceId || capability.instanceId === currentMembership?.instanceId)}
                saving={saving === "capability"}
                onCreateCapability={
                  canManageCapabilities
                    ? ((payload) =>
                        withSaving("capability", () =>
                          createCoreNetworkCapability({
                            ...payload,
                            memberId: member.id,
                            membershipId: currentMembership?.id,
                            instanceUniqueId,
                          })
                        ))
                    : null
                }
              />
            </Grid>
            <Grid item xs={12} lg={6}>
              <CoreNetworkCapacityPanel
                slots={safeArray(member.capacitySlots).filter((slot) => !slot.instanceId || slot.instanceId === currentMembership?.instanceId)}
                saving={saving === "capacity"}
                onSaveSlot={
                  canManageCapabilities
                    ? ((payload) =>
                        withSaving("capacity", () =>
                          upsertCoreNetworkCapacity({
                            ...payload,
                            memberId: member.id,
                            membershipId: currentMembership?.id,
                            instanceUniqueId,
                          })
                        ))
                    : null
                }
              />
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <CoreNetworkAssignmentPanel assignments={assignmentSummary.activeAssignments.map((item) => ({
                ...item,
                member,
                membership: currentMembership,
              }))} canViewPayment={canManagePayment} />
            </Grid>
            <Grid item xs={12} lg={6}>
              <SectionCard title="Completed assignments" subtitle="Historical completed work for this member in the current instance.">
                {assignmentSummary.completedAssignments.length ? (
                  <Stack spacing={1.5}>
                    {assignmentSummary.completedAssignments.map((assignment) => (
                      <Stack key={assignment.id} spacing={0.5} sx={{ p: 2, borderRadius: 2, border: "1px solid #edf1f7" }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {assignment.workRequest?.title || `Work Request #${assignment.workRequestId}`}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {toTitleCase(assignment.status)} | {formatDateTime(assignment.completedAt)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No completed assignments for this instance yet.
                  </Typography>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          <CoreNetworkRatingPanel
            assignments={[]}
            ratings={ratings}
            ratingSummary={ratingSummary}
          />

          {canManagePayment ? (
            <SectionCard title="Payout Tracking" subtitle="Instance-scoped payout history for this provider.">
              <PagedTable
                rows={payouts}
                columns={[
                  {
                    key: "workRequestId",
                    label: "Work Request",
                    render: (row) => row.workRequest?.title || `Work Request #${row.workRequestId}`,
                  },
                  { key: "status", label: "Status", render: (row) => <StatusChip value={row.status} /> },
                  { key: "amount", label: "Amount", render: (row) => formatCurrency(row.amount, row.currency || "CAD") },
                  { key: "payoutMethod", label: "Method", render: (row) => row.payoutMethod || "-" },
                  { key: "approvedAt", label: "Approved", render: (row) => formatDateTime(row.approvedAt) },
                  { key: "paidAt", label: "Paid", render: (row) => formatDateTime(row.paidAt) },
                ]}
                emptyTitle="No payouts tracked yet"
                emptyDescription="Payouts for this provider will appear here once assignment payout records are created."
              />
            </SectionCard>
          ) : null}
        </Stack>
      ) : null}
    </CoreNetworkShell>
  );
}
